import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { getHairAnalysisJob, searchProducts } from '../api/endpoints';
import { useAtlas } from '../atlas/AtlasCell';
import { hairCoords } from '../atlas/coords';
import { HairResult } from '../analysis/PremiumResult';
import Loading from '../components/Loading';
import AIDisclaimerFooter from '../components/AIDisclaimerFooter';
import { navigateTo } from '../utils/navigation';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import type { HairAnalysisJob, ProductSearchResultItem } from '../types/api';
import { useReportExport } from '../reports/useReportExport';

const TEAL = '#2AA4A6';
const EASE: [number, number, number, number] = [0.22, 0.61, 0.36, 1];

/* ── Helpers ─────────────────────────────────────────────────────────── */

function getHairProductIcon(t: string): string {
  const l = t.toLowerCase();
  if (l.includes('mask') || l.includes('treatment')) return 'spa';
  if (l.includes('shampoo')) return 'soap';
  if (l.includes('conditioner') || l.includes('serum')) return 'opacity';
  if (l.includes('oil')) return 'water_drop';
  if (l.includes('spray') || l.includes('volumiz')) return 'air';
  if (l.includes('heat') || l.includes('protect')) return 'thermostat';
  if (l.includes('dye') || l.includes('color') || l.includes('ton')) return 'palette';
  if (l.includes('brush') || l.includes('comb')) return 'brush';
  return 'content_cut';
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="row-3" style={{ marginBottom: 'var(--s-4)' }}>
      <span style={{ width: 4, height: 18, borderRadius: 99, background: TEAL, flexShrink: 0 }} />
      <h2 className="t-label" style={{ margin: 0 }}>{children}</h2>
    </div>
  );
}

function ProductTypeCard({ type, hint, onClick }: { type: string; hint: string; onClick: () => void }) {
  return (
    <button className="product-type-card" onClick={onClick}>
      <div className="product-type-card__icon">
        <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>{getHairProductIcon(type)}</span>
      </div>
      <div className="product-type-card__body">
        <p className="product-type-card__name">{type}</p>
        <p className="product-type-card__hint">{hint}</p>
      </div>
      <div className="product-type-card__arrow">
        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>arrow_forward</span>
      </div>
    </button>
  );
}

/* ── Page ────────────────────────────────────────────────────────────── */

export default function HairAnalysisResultPage(): React.JSX.Element {
  const { exporting, exportReport, errorToast } = useReportExport();
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<HairAnalysisJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProductType, setSelectedProductType] = useState<string | null>(null);
  const [productResults, setProductResults] = useState<ProductSearchResultItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const prefersReduced = useReducedMotion();
  const pageIn: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: prefersReduced ? 0 : 0.38, ease: EASE } },
  };

  useEffect(() => {
    if (!id) return;
    getHairAnalysisJob(id)
      .then(res => { setJob(res.job); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [id]);

  // Fetch the generated atlas once; the sheet crops its photo cells from it.
  // Called before the early returns below so hook order stays stable.
  const { atlasUrl } = useAtlas(job?.result_image_path);

  const handleProductClick = async (type: string) => {
    setSelectedProductType(type);
    setLoadingProducts(true);
    setProductResults([]);
    try {
      const results = await searchProducts({ product_keywords: type, limit: 10 });
      setProductResults(results);
    } catch { /* empty state shown */ }
    finally { setLoadingProducts(false); }
  };

  if (loading) return <Loading />;

  if (!job) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-8)', textAlign: 'center', minHeight: '60vh' }}>
        <h2 className="t-h2" style={{ margin: '0 0 var(--s-3)' }}>Analysis not found</h2>
        <p className="t-small t-mute" style={{ marginBottom: 'var(--s-6)' }}>We couldn&apos;t locate this hair analysis report.</p>
        <button onClick={() => { navigateTo('/'); }} className="btn btn--primary">Back to Dashboard</button>
      </div>
    );
  }

  if (job.status !== 'done') {
    navigateTo(`/hair-analysis/${id}/pending`);
    return <Loading />;
  }

  const result = job.result;
  const shortId = id?.slice(0, 8).toUpperCase() ?? '';
  const dateStr = new Date(job.submitted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const recommendations = result?.recommended_product_types?.filter((p) => p.trim().length > 0) ?? [];

  return (
    <motion.div initial="hidden" animate="visible" variants={pageIn} className="stack-7"
      style={{ maxWidth: 560, marginInline: 'auto', width: '100%', paddingBottom: 'var(--s-10)' }}>

      {/* ── Premium result composition (the prototype layout) ── */}
      {result ? (
        <HairResult
          result={result}
          atlasUrl={atlasUrl}
          coords={hairCoords}
          imagePath={job.image_path}
          id={shortId}
          date={dateStr}
          onOpenPoster={() => { if (id) void exportReport('hair', id); }}
          exporting={exporting}
        />
      ) : (
        <div className="card card--bordered card--flat" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
          <p className="t-small t-mute">Analysis details unavailable.</p>
        </div>
      )}
      {errorToast}

      {/* ── Product Recommendations ── */}
      {recommendations.length > 0 && (
        <section className="stack-5">
          <SectionLabel>Recommended for You</SectionLabel>
          <p className="t-small t-mute" style={{ marginTop: 0 }}>Products matched to your hair profile — click any to browse real results.</p>
          <motion.div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--s-5)' }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
          >
            {recommendations.map((type, i) => (
              <motion.div key={i} variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}>
                <ProductTypeCard type={type} hint="Matched to your hair profile." onClick={() => handleProductClick(type)} />
              </motion.div>
            ))}
          </motion.div>
        </section>
      )}

      <AIDisclaimerFooter generatedPreviews />

      {/* ── Product search modal ── */}
      {selectedProductType && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div className="modal" style={{ zIndex: 110 }}>
          <div
            className="modal__backdrop"
            role="button"
            tabIndex={0}
            aria-label="Close dialog"
            onClick={() => { setSelectedProductType(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedProductType(null);
              }
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.18, ease: EASE }}
            className="modal__card modal__card--lg"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
          >
            <div className="modal__header">
              <h3 className="modal__title" style={{ textTransform: 'capitalize' }}>{selectedProductType}</h3>
              <button onClick={() => { setSelectedProductType(null); }} className="btn btn--icon" aria-label="Close">
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
              </button>
            </div>
            <div className="modal__scroll">
              {loadingProducts ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s-9)' }}>
                  <span className="material-symbols-outlined t-mute animate-spin" style={{ fontSize: '32px' }}>progress_activity</span>
                </div>
              ) : productResults.length > 0 ? (
                <div className="stack-3">
                  {productResults.map((product, idx) => (
                    <a key={idx} href={product.product_link} target="_blank" rel="noreferrer" className="product-result">
                      <div className="product-result__inner">
                        {product.product_img_url ? (
                          <img src={product.product_img_url} alt={product.product_name} className="product-result__img" style={{ objectFit: 'contain' }} />
                        ) : (
                          <div className="product-result__img">
                            <span className="material-symbols-outlined">image</span>
                          </div>
                        )}
                        <div className="product-result__body">
                          <p className="product-result__name">{product.product_name}</p>
                          {product.product_price && (
                            <p className="product-result__price">
                              {typeof product.product_price === 'number' ? `$${product.product_price}` : product.product_price}
                            </p>
                          )}
                          <p className="product-result__desc">{product.product_description ?? 'No description available.'}</p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--s-9)', textAlign: 'center', gap: 'var(--s-4)' }}>
                  <div className="icon-ring">
                    <span className="material-symbols-outlined">inventory_2</span>
                  </div>
                  <p className="t-small t-mute">No products found for this category.</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>,
        document.body
      )}

    </motion.div>
  );
}
